import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Scrollbar from "../../../../components/scrollbar";
import Lightbox from "../../../../components/lightbox";
import ChatMessageItem from "./ChatMessageItem";
import { IChatConversation } from "../../../../@types/chat";
import {
  ChatAuthorBlock,
  ChatDayGroup,
  ChatMessage,
  ChatSender,
} from "../../../../@types/chat-message";
import { chatWallpaperSx } from "./chatStyles";
import { buildToolResultMap, messageToolCallFailed } from "./messageToolResults";
import { fDayKey, fDayLabel } from "src/utils/formatChatTime";

// ----------------------------------------------------------------------

type RawMessage = any;

type Props = {
  conversation: IChatConversation;
  history: RawMessage[];
};

/**
 * Funções de mensagem do assistente que viram bolha na tela. Os demais
 * tool_calls (buscarNaBase, criarPedido, listarItens, executarWebhook…) são
 * passos intermediários do function calling e continuam ocultos — a lista é
 * positiva justamente para que eles não virem lixo visual.
 */
const RENDERABLE_TOOLS = [
  "mensagemSimples",
  "mensagemComBotoes",
  "mensagemCatalogo",
  "mensagemChavePix",
  "mensagemOtp",
];

/** Mensagens do mesmo autor separadas por mais que isso viram blocos distintos. */
const GROUP_MAX_GAP_MS = 5 * 60 * 1000;

// ----------------------------------------------------------------------
// Identidade do remetente
// ----------------------------------------------------------------------

function resolveSender(raw: RawMessage, role: string): ChatSender {
  if (role === "client") return { kind: "client", label: null };
  if (role === "system") return { kind: "system", label: null };

  const sender = typeof raw?.sender === "string" ? raw.sender.trim() : "";

  if (sender === "Direto do whatsapp") {
    return { kind: "whatsapp", label: "WhatsApp" };
  }
  if (sender === "Integracao") return { kind: "integration", label: "Integração" };
  if (!sender || sender === "assistant") {
    return { kind: "assistant", label: "Alfred" };
  }

  // Qualquer outro valor é o nome do usuário logado no painel (req.user.name).
  return { kind: "panel", label: sender };
}

// ----------------------------------------------------------------------
// Key estável
// ----------------------------------------------------------------------

/** FNV-1a 32 bits. Determinístico e sem dependência. */
function hashString(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36);
}

/**
 * O backend devolve `.slice("historicChat", -100)` — uma janela deslizante.
 * Usar o índice do array como key faria o React remontar a lista inteira a cada
 * mensagem nova depois da centésima, matando o scroll e o áudio tocando. Já a
 * tripla (role, timestamp, conteúdo) é imutável para uma mensagem persistida.
 * O sufixo `#n` cobre duplicatas legítimas (ex.: "Oi" duas vezes no mesmo
 * segundo), e é estável porque o backend preserva a ordem.
 */
function createKeyFactory() {
  const seen = new Map<string, number>();

  return function buildKey(role: string, timestamp: any, fingerprint: string) {
    const base = `${role}|${timestamp ?? "sem-ts"}|${hashString(fingerprint)}`;
    const occurrence = seen.get(base) ?? 0;
    seen.set(base, occurrence + 1);
    return occurrence === 0 ? base : `${base}#${occurrence}`;
  };
}

// ----------------------------------------------------------------------
// Helpers de parsing
// ----------------------------------------------------------------------

const asText = (value: any): string | null => {
  if (typeof value === "string" && value.trim().length > 0) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
};

const asNumber = (value: any): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const buildMedia = (url: string, expires: any) => {
  const expiresAt = asNumber(expires);
  return {
    url,
    expiresAt,
    isExpired: expiresAt !== null && Date.now() > expiresAt,
  };
};

/** `📍 Localização enviada:\nLatitude: …\nLongitude: …\nNome: …\nEndereço: …` */
function parseLocationText(text: string) {
  if (!/^📍\s*Localização enviada:/.test(text)) return null;

  const pick = (label: string) => {
    const match = new RegExp(`^${label}:\\s*(.+)$`, "m").exec(text);
    return match ? match[1].trim() || null : null;
  };

  return {
    latitude: pick("Latitude"),
    longitude: pick("Longitude"),
    name: pick("Nome"),
    address: pick("Endereço"),
  };
}

/** `descrição da imagem: …\n\nmensagem do cliente: …` */
function parseImageDescription(text: string) {
  if (!/^descrição da imagem:/i.test(text)) return null;

  const [, description = "", caption = ""] =
    /^descrição da imagem:\s*([\s\S]*?)(?:\n\nmensagem do cliente:\s*([\s\S]*))?$/i.exec(
      text
    ) || [];

  const cleaned = description.trim();
  if (!cleaned) return null;

  return { description: cleaned, caption: caption.trim() || null };
}

/** Campanha e follow-up gravam um texto sintético longo com a mensagem dentro. */
function parseSystemText(text: string) {
  const campaign = /^Campanha enviada para este usuário\./.test(text);
  const followUp = /^Follow-up enviado para este usuario\./.test(text);

  if (!campaign && !followUp) {
    return { variant: "internal" as const, title: "Aviso interno", text };
  }

  const nameMatch = /(?:Campanha|Follow-up):\s*"([\s\S]*?)"\./.exec(text);
  const messageMatch = /Mensagem:\s*"([\s\S]*)"\s*$/.exec(text);
  const label = campaign ? "Campanha" : "Follow-up";

  return {
    variant: (campaign ? "campaign" : "followup") as "campaign" | "followup",
    title: nameMatch ? `${label}: ${nameMatch[1]}` : `${label} enviada`,
    text: messageMatch ? messageMatch[1] : null,
  };
}

/** Mídia do atendente vem como JSON stringificado dentro de `content`. */
function parseAttendantJson(content: any): Record<string, any> | null {
  if (content && typeof content === "object") return content;
  if (typeof content !== "string") return null;

  const trimmed = content.trim();
  if (!trimmed.startsWith("{")) return null;

  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

// ----------------------------------------------------------------------
// Normalização
// ----------------------------------------------------------------------

export function normalizeHistory(history: RawMessage[]): ChatMessage[] {
  const buildKey = createKeyFactory();
  const out: ChatMessage[] = [];
  const toolResults = buildToolResultMap(history);

  const push = (
    raw: RawMessage,
    role: ChatMessage["role"],
    fingerprint: string,
    rest: any
  ) => {
    out.push({
      id: buildKey(role, raw?.timestamp, fingerprint),
      role,
      sender: resolveSender(raw, role),
      timestamp: raw?.timestamp ?? null,
      ...rest,
    });
  };

  const pushUnknown = (
    raw: RawMessage,
    role: ChatMessage["role"],
    reason: string,
    preview: any
  ) => {
    const text = typeof preview === "string" ? preview : JSON.stringify(preview);
    push(raw, role, `unknown:${reason}:${text}`, {
      type: "unknown",
      reason,
      preview: (text || "").slice(0, 300),
    });
  };

  history.forEach((raw) => {
    try {
      // ---------------------------------------------------------- cliente
      if (raw?.role === "user") {
        const text = asText(raw.content);
        if (!text) return;

        const location = parseLocationText(text);
        if (location) {
          push(raw, "client", text, { type: "location", ...location });
          return;
        }

        const image = parseImageDescription(text);
        if (image) {
          push(raw, "client", text, { type: "image-description", ...image });
          return;
        }

        push(raw, "client", text, { type: "text", text });
        return;
      }

      // -------------------------------------------------------- assistente
      if (raw?.role === "assistant") {
        // Sem tool_calls o texto vem direto em `content`.
        if (!Array.isArray(raw.tool_calls)) {
          const text = asText(raw.content);
          if (!text) return;
          push(raw, "assistant", text, { type: "text", text });
          return;
        }

        raw.tool_calls.forEach((call: any) => {
          const name = call?.function?.name;
          // Passo intermediário do function calling: não é mensagem.
          if (!name || RENDERABLE_TOOLS.indexOf(name) === -1) return;
          // A chamada pode ter sido proposta junto de ações e rejeitada pelo
          // backend. Ela permanece no histórico técnico, mas nunca foi enviada.
          if (messageToolCallFailed(toolResults, call)) return;

          let args: any;
          try {
            args = JSON.parse(call.function.arguments);
          } catch {
            pushUnknown(
              raw,
              "assistant",
              `Argumentos ilegíveis em ${name}`,
              call?.function?.arguments
            );
            return;
          }

          if (name === "mensagemSimples") {
            // O backend manda `messages: string[]` e envia um item por vez no
            // WhatsApp (w-api.js:367-375) — cada item vira sua própria bolha.
            // Assistentes antigos ainda têm a forma legada `message: string`.
            const items = Array.isArray(args?.messages)
              ? args.messages
              : [args?.message];
            const texts = items.map(asText).filter(Boolean) as string[];

            if (!texts.length) {
              pushUnknown(raw, "assistant", "mensagemSimples sem texto", args);
              return;
            }

            texts.forEach((text) =>
              push(raw, "assistant", text, { type: "text", text })
            );
            return;
          }

          if (name === "mensagemComBotoes") {
            const sections = (Array.isArray(args?.sections) ? args.sections : []).map(
              (section: any) => ({
                title: asText(section?.title),
                rows: (Array.isArray(section?.rows) ? section.rows : [])
                  .map((row: any) => ({
                    title: asText(row?.title) || "",
                    description: asText(row?.description),
                  }))
                  .filter((row: any) => row.title),
              })
            );

            push(raw, "assistant", JSON.stringify(args), {
              type: "buttons",
              title: asText(args?.title),
              description: asText(args?.description),
              footerText: asText(args?.footerText),
              buttonText: asText(args?.buttonText),
              sections,
            });
            return;
          }

          if (name === "mensagemCatalogo") {
            const cards = (Array.isArray(args?.cards) ? args.cards : []).map(
              (card: any) => ({
                text: asText(card?.text),
                image: asText(card?.image),
                actions: (Array.isArray(card?.buttonActions)
                  ? card.buttonActions
                  : []
                ).map((action: any) => {
                  const type = asText(action?.type);
                  return {
                    actionType:
                      type === "URL" || type === "REPLAY" ? type : "UNKNOWN",
                    buttonText: asText(action?.buttonText) || "Selecionar",
                    url: asText(action?.url),
                  };
                }),
              })
            );

            push(raw, "assistant", JSON.stringify(args), {
              type: "catalog",
              intro: asText(args?.message),
              cards,
            });
            return;
          }

          if (name === "mensagemChavePix") {
            push(raw, "assistant", JSON.stringify(args), {
              type: "pix",
              pixKey: asText(args?.pixKey) || "",
              merchantName: asText(args?.merchantName),
              amount: asNumber(Number(args?.amount)),
            });
            return;
          }

          if (name === "mensagemOtp") {
            const code = asText(args?.code);
            if (!code) {
              pushUnknown(raw, "assistant", "mensagemOtp sem código", args);
              return;
            }

            push(raw, "assistant", JSON.stringify(args), {
              type: "otp",
              text: asText(args?.message),
              buttonText: asText(args?.buttonText) || "Copiar código",
              code,
            });
          }
        });
        return;
      }

      // --------------------------------------------------------- atendente
      if (raw?.role === "attendant") {
        const parsed = parseAttendantJson(raw.content);

        if (parsed) {
          const text = asText(parsed.texto);
          if (text) {
            push(raw, "attendant", text, { type: "text", text });
            return;
          }

          // Chaves de mídia gravadas por formatAttendantContent (w-api.js:576).
          const mediaKinds: Array<[string, ChatMessage["type"]]> = [
            ["imagem", "image"],
            ["sticker", "sticker"],
            ["gif", "gif"],
            ["audio", "audio"],
            ["video", "video"],
            ["documento", "document"],
          ];

          const found = mediaKinds
            .map(([key, type]) => ({ url: asText(parsed[key]), type }))
            .filter((item) => item.url)[0];

          if (found) {
            push(raw, "attendant", `${found.type}:${found.url}`, {
              type: found.type,
              media: buildMedia(found.url as string, parsed.expires),
            });
            return;
          }

          // Localização precisa vir explícita: aceitar qualquer objeto com
          // `address` criava localização fantasma para JSON não reconhecido.
          const location = parsed.localizacao;
          if (location && typeof location === "object") {
            push(raw, "attendant", JSON.stringify(location), {
              type: "location",
              latitude: asText(location.degreesLatitude),
              longitude: asText(location.degreesLongitude),
              name: asText(location.name),
              address: asText(location.address),
            });
            return;
          }

          pushUnknown(raw, "attendant", "Conteúdo não reconhecido", parsed);
          return;
        }

        const text = asText(raw.content);
        if (!text) return;
        push(raw, "attendant", text, { type: "text", text });
        return;
      }

      // ----------------------------------------------------------- sistema
      if (raw?.role === "system") {
        const text = asText(raw.content);
        if (!text) return;
        push(raw, "system", text, { type: "system", ...parseSystemText(text) });
        return;
      }

      // `role: "tool"` é retorno de função — ruído puro, nunca vai pra tela.
    } catch (error) {
      pushUnknown(raw, "system", "Falha ao interpretar a mensagem", raw?.content);
    }
  });

  return out;
}

// ----------------------------------------------------------------------
// Agrupamento por dia e por autor
// ----------------------------------------------------------------------

function groupMessages(messages: ChatMessage[]): ChatDayGroup[] {
  const days: ChatDayGroup[] = [];

  messages.forEach((message) => {
    const dayKey = fDayKey(message.timestamp);
    let day = days[days.length - 1];

    if (!day || day.dayKey !== dayKey) {
      day = { dayKey, label: fDayLabel(message.timestamp), blocks: [] };
      days.push(day);
    }

    // Mensagem de sistema nunca agrupa: é sempre um chip isolado.
    const authorKey =
      message.type === "system"
        ? "__system__"
        : `${message.role}|${message.sender.label ?? ""}`;

    const lastBlock: ChatAuthorBlock | undefined = day.blocks[day.blocks.length - 1];
    const lastMessage = lastBlock?.messages[lastBlock.messages.length - 1];

    const sameAuthor =
      !!lastBlock && lastBlock.authorKey === authorKey && authorKey !== "__system__";

    const withinGap =
      sameAuthor &&
      Math.abs(
        new Date(message.timestamp ?? 0).getTime() -
          new Date(lastMessage?.timestamp ?? 0).getTime()
      ) <= GROUP_MAX_GAP_MS;

    if (sameAuthor && withinGap) {
      lastBlock.messages.push(message);
      return;
    }

    day.blocks.push({ authorKey, messages: [message] });
  });

  return days;
}

// ----------------------------------------------------------------------

export default function ChatMessageList({ history }: Props) {
  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const messages = useMemo(() => normalizeHistory(history), [history]);
  const dayGroups = useMemo(() => groupMessages(messages), [messages]);

  /** Uma instância de lightbox para a lista inteira, não uma por bolha. */
  const slides = useMemo(() => {
    const out: { src: string }[] = [];
    const seen = new Set<string>();

    const add = (url: string | null) => {
      if (!url || seen.has(url)) return;
      seen.add(url);
      out.push({ src: url });
    };

    messages.forEach((message) => {
      if (message.type === "image" || message.type === "gif") {
        if (!message.media.isExpired) add(message.media.url);
        return;
      }
      if (message.type === "catalog") {
        message.cards.forEach((card) => add(card.image));
      }
    });

    return out;
  }, [messages]);

  const handleOpenLightbox = useCallback(
    (url: string) => {
      const index = slides.findIndex((slide) => slide.src === url);
      if (index >= 0) setLightboxIndex(index);
    },
    [slides]
  );

  // ================= AUTO SCROLL =================
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !shouldAutoScrollRef.current) return;

    el.scrollTop = el.scrollHeight;
  }, [messages]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;

    shouldAutoScrollRef.current = isAtBottom;
  };

  // ================= RENDER =================
  return (
    <>
      <Scrollbar
        scrollableNodeProps={{
          ref: scrollRef,
          onScroll: handleScroll,
        }}
        sx={{ px: 2, py: 1.5, height: 1, ...chatWallpaperSx(theme) }}
      >
        {dayGroups.map((day) => (
          <Box key={day.dayKey}>
            <Stack alignItems="center" sx={{ my: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  color: theme.palette.mode === "dark" ? "grey.300" : "grey.700",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(255,255,255,0.9)",
                  boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)",
                }}
              >
                {day.label}
              </Typography>
            </Stack>

            {day.blocks.map((block) =>
              block.messages.map((message, messageIndex) => (
                <ChatMessageItem
                  key={message.id}
                  message={message}
                  isFirstOfBlock={messageIndex === 0}
                  onOpenMedia={handleOpenLightbox}
                />
              ))
            )}
          </Box>
        ))}
      </Scrollbar>

      <Lightbox
        index={lightboxIndex}
        slides={slides}
        open={lightboxIndex >= 0}
        close={() => setLightboxIndex(-1)}
      />
    </>
  );
}
